/**
 * GMA-1995 — Command Center map toolbar for drawing an elopement boundary.
 *
 * Mounted only when REACT_APP_FEATURE_FLAG_ELOPEMENT_BOUNDARY_ENABLED=true
 * (commandcenter build). Caregiver homepage map never mounts this.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  closeElopementRing,
  uniqueVertices,
  validateClosedElopementRing,
} from "@graymattersalliance/compass-map";
import {
  deleteElopementLocation,
  fetchElopementLocation,
  saveElopementLocation,
} from "../../api/elopementBoundaryService";

/** Same ceiling as other map panel fetches — unbounded GET must not lock the toolbar. */
export const ELOPEMENT_LOAD_TIMEOUT_MS = 30000;

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        Object.assign(
          new Error(`Boundary load timed out after ${ms}ms. Try again.`),
          { name: "ElopementLoadTimeout" },
        ),
      );
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

const panelStyle = {
  display: "grid",
  gap: 8,
  padding: "10px 12px",
  background: "#141A22",
  border: "1px solid #2A3444",
  borderRadius: 10,
  color: "#E8EEF5",
  fontSize: 13,
};

const btn = (primary) => ({
  minHeight: 36,
  padding: "6px 12px",
  borderRadius: 8,
  border: primary ? "none" : "1px solid #2A3444",
  background: primary ? "#3EA6FF" : "transparent",
  color: primary ? "#0A0D13" : "#E8EEF5",
  fontWeight: 600,
  cursor: "pointer",
});

export default function ElopementBoundaryControls({
  clients,
  ring,
  drawMode,
  onRingChange,
  onDrawModeChange,
  onStatus,
}) {
  const [clientId, setClientId] = useState("");
  const [busy, setBusy] = useState(false);
  // Mutations stay off until a load settles successfully (404 = empty ring
  // counts as success). A failed load must not enable Draw/Save/Clear over an
  // unknown server state.
  const [boundaryKnown, setBoundaryKnown] = useState(false);
  const options = useMemo(() => {
    const list = Array.isArray(clients) ? clients : [];
    return list
      .filter((c) => c && c.id !== null && c.id !== undefined)
      .map((c) => ({
        id: String(c.id),
        label: c.name || c.label || `Client ${c.id}`,
      }));
  }, [clients]);

  useEffect(() => {
    if (!clientId) {
      setBusy(false);
      setBoundaryKnown(false);
      onRingChange([]);
      onDrawModeChange(false);
      onStatus("");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setBusy(true);
      setBoundaryKnown(false);
      // Drop the prior client's ring/draw immediately so the map cannot keep
      // editing A's fence while the selector already shows B (and so an
      // unbounded GET cannot leave that mismatch on screen forever).
      onRingChange([]);
      onDrawModeChange(false);
      onStatus("Loading saved boundary…");
      try {
        const loaded = await withTimeout(
          fetchElopementLocation(clientId),
          ELOPEMENT_LOAD_TIMEOUT_MS,
        );
        if (cancelled) return;
        onRingChange(loaded || []);
        onDrawModeChange(false);
        setBoundaryKnown(true);
        onStatus(
          loaded
            ? "Loaded saved boundary. Draw to replace, or clear it."
            : "No saved boundary. Click Draw, then click the map to place corners.",
        );
      } catch (err) {
        if (cancelled) return;
        onRingChange([]);
        onDrawModeChange(false);
        setBoundaryKnown(false);
        onStatus(
          err && err.message
            ? err.message
            : "Could not load the saved boundary. Nothing was changed.",
        );
      } finally {
        // Skip clear when cancelled — the next effect (or the empty-client
        // branch above) owns busy so a rapid switch cannot leave us stuck.
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, onRingChange, onDrawModeChange, onStatus]);

  const uniqueCount = uniqueVertices(ring).length;
  const closedOk = !validateClosedElopementRing(ring);

  const startDraw = useCallback(() => {
    onRingChange([]);
    onDrawModeChange(true);
    onStatus("Drawing: click the map to add corners (at least 3), then Close shape.");
  }, [onRingChange, onDrawModeChange, onStatus]);

  const undo = useCallback(() => {
    // Pop only the last vertex as drawn — do not collapse duplicates first,
    // or a double-click [A,B,B] would jump to [A] on one Undo.
    if (!Array.isArray(ring) || ring.length === 0) return;
    onRingChange(ring.slice(0, -1));
  }, [ring, onRingChange]);

  const closeShape = useCallback(() => {
    const closed = closeElopementRing(ring);
    if (!closed) {
      onStatus("Need at least three corners before closing the shape.");
      return;
    }
    onRingChange(closed);
    onDrawModeChange(false);
    onStatus("Shape closed. Save to store it for leave-area alerts (backend GMA-1996).");
  }, [ring, onRingChange, onDrawModeChange, onStatus]);

  const save = useCallback(async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      await saveElopementLocation(clientId, ring);
      onStatus("Boundary saved.");
    } catch (err) {
      onStatus(err && err.message ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }, [clientId, ring, onStatus]);

  const clear = useCallback(async () => {
    if (!clientId) return;
    setBusy(true);
    try {
      await deleteElopementLocation(clientId);
      onRingChange([]);
      onDrawModeChange(false);
      onStatus("Boundary cleared.");
    } catch (err) {
      onStatus(err && err.message ? err.message : "Clear failed.");
    } finally {
      setBusy(false);
    }
  }, [clientId, onRingChange, onDrawModeChange, onStatus]);

  return (
    <div data-testid="elopement-boundary-controls" style={panelStyle}>
      <div style={{ fontWeight: 700 }}>Elopement boundary</div>
      <div style={{ color: "#9AA8BC", fontSize: 12, lineHeight: 1.4 }}>
        Select a client → Draw → click the map to place corners (at least 3) →
        Close shape → Save. This is a freeform safe area (not the circular home
        zone). Leave-area alerts use this ring once the backend is enabled.
      </div>
      <label style={{ display: "grid", gap: 4 }}>
        Client
        <select
          data-testid="elopement-client-select"
          value={clientId}
          disabled={busy}
          onChange={(e) => setClientId(e.target.value)}
          style={{ minHeight: 36, borderRadius: 8, padding: "4px 8px" }}
        >
          <option value="">Select a client…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <button
          type="button"
          style={btn(false)}
          disabled={!clientId || busy || !boundaryKnown}
          onClick={startDraw}
        >
          Draw
        </button>
        <button
          type="button"
          style={btn(false)}
          disabled={!drawMode || !Array.isArray(ring) || ring.length === 0 || busy}
          onClick={undo}
        >
          Undo point
        </button>
        <button
          type="button"
          style={btn(false)}
          disabled={!drawMode || uniqueCount < 3 || busy}
          onClick={closeShape}
        >
          Close shape
        </button>
        <button
          type="button"
          style={btn(true)}
          disabled={!clientId || !closedOk || busy || !boundaryKnown}
          onClick={save}
        >
          Save
        </button>
        <button
          type="button"
          style={btn(false)}
          disabled={!clientId || busy || !boundaryKnown}
          onClick={clear}
        >
          Clear
        </button>
      </div>
      <div style={{ color: "#9AA8BC", fontSize: 12 }}>
        {drawMode
          ? `Drawing · ${uniqueCount} corner${uniqueCount === 1 ? "" : "s"}`
          : closedOk
            ? "Closed boundary ready"
            : "Idle"}
      </div>
    </div>
  );
}

ElopementBoundaryControls.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object),
  ring: PropTypes.array,
  drawMode: PropTypes.bool,
  onRingChange: PropTypes.func.isRequired,
  onDrawModeChange: PropTypes.func.isRequired,
  onStatus: PropTypes.func.isRequired,
};
